/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { SiteManagerPage } from "../SiteManagerPage";
import { PageProps } from "../../../models/PageProps";
import { petSpecies, strings } from "../../../common/Strings/Strings";
import SpeciesApi from "../../../api/SpeciesApi";
import logger from "../../../util/logger";
import AlertBox from "../../../components/AlertBox";
import { generateOptions, getGeneralError } from "../../../util/helperFunctions";
import Button from "../../../components/Button";
import { PencilSquare } from "../../../common/Icons/PencilSquare";
import AddSpeciesModal from "./AddSpeciesModal";
import EditSpeciesModal from "./EditSpeciesModal";
import { AdminSpeciesResponse } from "../../../models/species/AdminSpeciesResponse";
import EmptyListText from "../../../components/EmptyListText";
import { AnimalSpecies } from "../../../common/AnimalSpecies/AnimalSpecies";
import { useSpecies } from "../../../contexts/SpeciesContext";
import { SwedishSpeciesCategory } from "../../../models/species/SwedishSpeciesCategory";

const SpeciesPage: React.FC<PageProps> = (props: PageProps) => {
  const [error, setError] = useState<string | null>(null);
  const [isAddModaOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [speciesToEdit, setSpeciesToEdit] = useState<AdminSpeciesResponse>();
  const { setPageLoading } = props;
  const [species, setSpecies] = useState<AdminSpeciesResponse[]>([]);
  const [reload, setReload] = useState<boolean>(false);
  const { speciesCodesAndNames } = useSpecies();

  useEffect(() => {
    const getSpecies = async () => {
      setPageLoading(true);
      try {
        const resp = await SpeciesApi.getAllSpecies();
        setSpecies(resp.data);
      } catch (e) {
        logger.error(e);
        setError(await getGeneralError(e));
      } finally {
        setPageLoading(false);
      }
    };

    void getSpecies();
  }, [reload]);

  const getSwedishCategory = (species?: SwedishSpeciesCategory) =>{
    const svTextAndCode = generateOptions(AnimalSpecies, "species", "code");
    if(speciesCodesAndNames && species){
      const merged = speciesCodesAndNames.map(scan => {
      const match = svTextAndCode.find(text => text.value === scan.code);
          return { ...scan, ...(match || {}) };
      });

      const title = merged.find(item => item.petSpecies === species.toString())?.title;
      const code = merged.find(item => item.petSpecies === species.toString())?.code;

      return title + " (" + code + ")";
    }
  };

  return (
    <div className="space-y-6">
      <AlertBox message={error} />
      <Button
        minW
        onClick={() => {
          setIsAddModalOpen(true);
        }}
      >
        {strings.addSpecies}
      </Button>
      <div className="tw-table-container">
        <table className="tw-table">
          <thead className="tw-thead">
            <tr>
              <th className="tw-th" />
              <th className="tw-th">{strings.latinName}</th>
              <th className="tw-th">{strings.name} (EN)</th>
              <th className="tw-th">{strings.name} (HU)</th>
              <th className="tw-th">{strings.name} (SV)</th>
              <th className="tw-th">{strings.isProducingAnimal}</th>
              <th className="tw-th">{strings.fromClinic}</th>
              <th className="tw-th">{strings.swedishPrescriptionCategory}</th>
              <th className="tw-th">{strings.petVetDataSpeciesCategory}</th>
              <th className="tw-th" />
            </tr>
          </thead>
          <tbody className="tw-tbody">
            {
              species.length === 0 ?
              <tr>
                <td colSpan={8} className="p-4 text-sm font-medium"><EmptyListText /></td>
              </tr>
              :
            species.map((s: AdminSpeciesResponse, index) => (
              <tr>
                <td className="px-4 py-3 text-sm font-medium">{index+1}</td>
                <td className="px-4 py-3 text-sm font-medium">{s.latinName}</td>
                <td className="px-4 py-3 text-sm font-medium">
                  {s.localizedNames?.["en"]}
                </td>
                <td className="px-4 py-3 text-sm font-medium">
                  {s.localizedNames?.["hu"]}
                </td>
                <td className="px-4 py-3 text-sm font-medium">
                  {s.localizedNames?.["sv"]}
                </td>
                <td className="px-4 py-3 text-sm font-medium whitespace-nowrap">
                  {s.isProducingAnimal ? strings.yes : strings.no}
                </td>
                <td className="px-4 py-3 text-sm font-medium whitespace-nowrap">
                  {s.clinicId}
                </td>
                <td className="px-4 py-3 text-sm font-medium whitespace-nowrap">
                  {getSwedishCategory(s.swedishSpeciesCategory)}
                </td>
                <td className="px-4 py-3 text-sm font-medium whitespace-nowrap">
                  {s.petVetDataSpeciesCategory && petSpecies[s.petVetDataSpeciesCategory]}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-right">
                  <Button
                    onClick={() => {
                      setSpeciesToEdit(s);
                    }}
                    variant="icon"
                  >
                    <PencilSquare />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AddSpeciesModal
        isOpen={isAddModaOpen}
        close={(isReload?: boolean) => {
          setIsAddModalOpen(false);
          if(isReload){
            setReload(prev => !prev);
          }
        }}
      />
      <EditSpeciesModal
        close={(isReload?: boolean) => {
          setSpeciesToEdit(undefined);
          if(isReload){
            setReload(prev => !prev);
          }
        }}
        isOpen={!!speciesToEdit}
        species={speciesToEdit}
      />
    </div>
  );
};

export default SiteManagerPage(SpeciesPage, strings.SPECIES);
